import { useState } from "react";
import { Crosshair, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";

export const useDeviceLocation = () => {
  const [busy, setBusy] = useState(false);
  const locate = (onOk) => {
    if (!navigator.geolocation) { toast.error("La géolocalisation n'est pas disponible sur cet appareil"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setBusy(false); onOk({ latitude: +pos.coords.latitude.toFixed(6), longitude: +pos.coords.longitude.toFixed(6), label: "Position de l'appareil" }); },
      (err) => { setBusy(false); toast.error(err.code === 1 ? "Géolocalisation refusée : vous pouvez rechercher une adresse" : "Position introuvable, essayez la recherche d'adresse"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  };
  return { busy, locate };
};

export default function LocationSearch({ onPick, prefix = "location", deviceLabel = "Me localiser" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const device = useDeviceLocation();

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.get(`/public/geocode?q=${encodeURIComponent(q)}`); setResults(data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" variant="outline" onClick={() => device.locate(onPick)} disabled={device.busy} data-testid={`${prefix}-device-button`}>
          {device.busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Crosshair className="mr-1.5 h-4 w-4" />}{deviceLabel}
        </Button>
        <form onSubmit={search} className="flex flex-1 gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ou recherchez une adresse, un quartier, une ville…" className="h-10" data-testid={`${prefix}-address-input`} />
          <Button type="submit" variant="secondary" disabled={loading || q.trim().length < 3} data-testid={`${prefix}-address-search`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>
      </div>
      {results && (
        <ul className="max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white text-sm" data-testid={`${prefix}-address-results`}>
          {!results.length && <li className="px-3 py-2 text-slate-500">Aucune adresse trouvée</li>}
          {results.map((r, i) => (
            <li key={`${r.latitude}-${r.longitude}-${i}`}>
              <button type="button" className="w-full px-3 py-2 text-left hover:bg-slate-50" data-testid={`${prefix}-address-result-${i}`}
                onClick={() => { onPick(r); setResults(null); }}>{r.label}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
